import React, { useRef, useEffect, useState } from 'react';

/**
 * WYSIWYG Rich Text Editor using contentEditable
 * A simple, reliable rich text editor that works like Word/Email
 */
export function RichTextEditor({ 
  value, 
  onChange, 
  placeholder = 'Start typing...',
  minHeight = '200px'
}) {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Update editor content when value changes externally
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleKeyDown = (e) => {
    // Handle Tab key for indentation
    if (e.key === 'Tab') {
      e.preventDefault();
      execCommand('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
  };

  return (
    <div className="rich-text-editor border rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-muted/50 border-b">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="p-2 hover:bg-muted rounded font-bold text-sm"
          title="Bold (Ctrl+B)"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="p-2 hover:bg-muted rounded italic text-sm"
          title="Italic (Ctrl+I)"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="p-2 hover:bg-muted rounded underline text-sm"
          title="Underline (Ctrl+U)"
        >
          U
        </button>
        <div className="w-px bg-border mx-1" />
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="p-2 hover:bg-muted rounded text-sm"
          title="Bullet List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="p-2 hover:bg-muted rounded text-sm"
          title="Numbered List"
        >
          1. List
        </button>
        <div className="w-px bg-border mx-1" />
        <button
          type="button"
          onClick={() => {
            const url = prompt('Enter URL:');
            if (url) execCommand('createLink', url);
          }}
          className="p-2 hover:bg-muted rounded text-sm text-blue-600"
          title="Insert Link"
        >
          🔗 Link
        </button>
        <button
          type="button"
          onClick={() => execCommand('removeFormat')}
          className="p-2 hover:bg-muted rounded text-sm text-muted-foreground"
          title="Clear Formatting"
        >
          Clear
        </button>
      </div>
      
      {/* Editor Area */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          className="p-3 outline-none prose prose-sm max-w-none"
          style={{ 
            minHeight, 
            lineHeight: '1.6',
            fontFamily: 'inherit'
          }}
          suppressContentEditableWarning
        />
        {/* Placeholder */}
        {!value && !isFocused && (
          <div 
            className="absolute top-3 left-3 text-muted-foreground pointer-events-none"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

export default RichTextEditor;
